export default function zohoSignHelper() {
  // Get Zoho portal URL for a document
  const getZohoPortalUrl = documentId => {
    if (!documentId) return null
    // Zoho portal URL pattern: https://sign.zoho.eu/zs/{account_id}#/request/details/{document_id}
    const ZOHO_ACCOUNT_ID = '20103098086' // Your Zoho account ID
    return `https://sign.zoho.eu/zs/${ZOHO_ACCOUNT_ID}#/request/details/${documentId}`
  }

  // Get all contracts for an offer with storage details
  const getOfferContracts = (offerId, zohoContracts, storages) => {
    const contracts = zohoContracts.filter(contract => contract.offer === offerId || contract.offer?.id === offerId)

    return contracts.map(contract => {
      const storage = storages.find(s => s.id === contract.storage || s.id === contract.storage?.id)

      return {
        id: contract.id,
        storageIdent: storage?.storageIdent || 'Unknown Storage',
        storageId: storage?.id || contract.storage,
        status: contract.zohoSignStatus || 'not_sent',
        documentId: contract.zohoDocumentId,
        requestId: contract.zohoRequestId,
        signUrl: contract.zohoSignUrl,
        sentDate: contract.zohoSentDate,
        recipientEmail: contract.zohoSignerEmail,
        recipientName: contract.zohoSignerName,
        actionId: contract.zohoActionId,
        zohoUrl: getZohoPortalUrl(contract.zohoDocumentId)
      }
    })
  }

  // Get all contracts for a storage (historical)
  const getStorageContractHistory = (storageId, zohoContracts) => zohoContracts
    .filter(contract => contract.storage === storageId || contract.storage?.id === storageId)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .map(contract => ({
      ...contract,
      zohoUrl: getZohoPortalUrl(contract.zohoDocumentId)
    }))

  // Get all contracts for a customer
  const getCustomerContracts = (customerId, zohoContracts, storages) => {
    const contracts = zohoContracts.filter(contract => contract.customer === customerId || contract.customer?.id === customerId)

    return contracts.map(contract => {
      const storage = storages.find(s => s.id === contract.storage || s.id === contract.storage?.id)

      return {
        ...contract,
        storageIdent: storage?.storageIdent || 'Unknown Storage',
        zohoUrl: getZohoPortalUrl(contract.zohoDocumentId)
      }
    })
  }

  // Get contracts that need status updates (not final states)
  const getActiveContracts = zohoContracts => zohoContracts.filter(contract => contract.zohoDocumentId
      && !['signed', 'declined', 'expired'].includes(contract.zohoSignStatus))

  // Get contract statistics for an offer
  const getOfferContractStats = (offerId, zohoContracts, storages) => {
    const contracts = getOfferContracts(offerId, zohoContracts, storages)
    const totalStorages = storages.length
    const contractsSent = contracts.length
    const contractsSigned = contracts.filter(c => c.status === 'signed').length
    const contractsViewed = contracts.filter(c => c.status === 'viewed').length
    const contractsPending = contracts.filter(c => ['sent', 'viewed'].includes(c.status)).length

    return {
      total: totalStorages,
      sent: contractsSent,
      signed: contractsSigned,
      viewed: contractsViewed,
      pending: contractsPending,
      notSent: totalStorages - contractsSent
    }
  }

  // Check if offer has any contracts
  const hasContracts = (offerId, zohoContracts) => zohoContracts.some(contract => (contract.offer === offerId || contract.offer?.id === offerId)
      && contract.zohoDocumentId)

  // Get status badge variant for display
  const getStatusVariant = status => {
    const variants = {
      not_sent: 'secondary',
      sent: 'info',
      viewed: 'warning',
      signed: 'success',
      declined: 'danger',
      expired: 'dark'
    }
    return variants[status] || 'secondary'
  }

  // Get status label in German
  const getStatusLabel = status => {
    const labels = {
      not_sent: 'Nicht versendet',
      sent: 'Versendet',
      viewed: 'Angesehen',
      signed: 'Unterzeichnet',
      declined: 'Abgelehnt',
      expired: 'Abgelaufen'
    }
    return labels[status] || 'Unbekannt'
  }

  // Extract Zoho document ID from API response
  const extractDocumentId = zohoResponse => {
    console.log('🔍 extractDocumentId input:', zohoResponse)

    if (!zohoResponse || !zohoResponse.data) {
      console.warn('❌ extractDocumentId: Missing zohoResponse or zohoResponse.data')
      return null
    }

    const zohoData = zohoResponse.data
    console.log('🔍 zohoData structure:', zohoData)
    console.log('🔍 zohoData as JSON string:', JSON.stringify(zohoData, null, 2))

    // Try multiple possible response structures
    let documentId = null

    // Priority 1: Use request_id (this is the actual Zoho Sign document ID)
    if (zohoData.requests && zohoData.requests.request_id) {
      documentId = zohoData.requests.request_id
      console.log('📄 Found documentId via requests.request_id (Zoho Sign ID):', documentId)
    }

    // Structure 1: zohoData.requests (expected from middleware transformation)
    if (!documentId && zohoData.requests && zohoData.requests.document_ids) {
      documentId = zohoData.requests.document_ids?.[0]?.document_id
      console.log('📄 Found documentId via requests.document_ids:', documentId)
    }

    // Structure 2: Direct in zohoData (if middleware passes through directly)
    if (!documentId && zohoData.document_ids) {
      documentId = zohoData.document_ids?.[0]?.document_id
      console.log('📄 Found documentId via direct document_ids:', documentId)
    }

    // Structure 3: In requests array
    if (!documentId && Array.isArray(zohoData.requests)) {
      const firstRequest = zohoData.requests[0]
      if (firstRequest && firstRequest.document_ids) {
        documentId = firstRequest.document_ids?.[0]?.document_id
        console.log('📄 Found documentId via requests[0].document_ids:', documentId)
      }
    }

    // Structure 4: Direct request_id field in zohoData
    if (!documentId && zohoData.request_id) {
      documentId = zohoData.request_id
      console.log('📄 Found documentId via direct request_id:', documentId)
    }

    // Structure 5: Direct document_id field in zohoData (fallback)
    if (!documentId && zohoData.document_id) {
      documentId = zohoData.document_id
      console.log('📄 Found documentId via direct document_id:', documentId)
    }

    // Structure 6: In requests object with direct document_id
    if (!documentId && zohoData.requests && zohoData.requests.document_id) {
      documentId = zohoData.requests.document_id
      console.log('📄 Found documentId via requests.document_id:', documentId)
    }

    // Structure 7: Nested in response data (request_id first)
    if (!documentId && zohoData.response && zohoData.response.request_id) {
      documentId = zohoData.response.request_id
      console.log('📄 Found documentId via response.request_id:', documentId)
    }

    // Structure 8: Nested in response data (document_id fallback)
    if (!documentId && zohoData.response && zohoData.response.document_id) {
      documentId = zohoData.response.document_id
      console.log('📄 Found documentId via response.document_id:', documentId)
    }

    // Structure 9: In data object (request_id first)
    if (!documentId && zohoData.data && zohoData.data.request_id) {
      documentId = zohoData.data.request_id
      console.log('📄 Found documentId via data.request_id:', documentId)
    }

    // Structure 10: In data object (document_id fallback)
    if (!documentId && zohoData.data && zohoData.data.document_id) {
      documentId = zohoData.data.document_id
      console.log('📄 Found documentId via data.document_id:', documentId)
    }

    // Structure 11: Search recursively for request_id first, then document_id
    if (!documentId) {
      const findDocumentId = (obj, path = '') => {
        if (typeof obj !== 'object' || obj === null) return null

        const entries = Object.entries(obj)

        // First pass: look for request_id
        for (let i = 0; i < entries.length; i += 1) {
          const [key, value] = entries[i]
          const currentPath = path ? `${path}.${key}` : key

          if (key === 'request_id' && typeof value === 'string') {
            console.log(`📄 Found documentId via recursive search (request_id) at ${currentPath}:`, value)
            return value
          }
        }

        // Second pass: look for document_id
        for (let i = 0; i < entries.length; i += 1) {
          const [key, value] = entries[i]
          const currentPath = path ? `${path}.${key}` : key

          if (key === 'document_id' && typeof value === 'string') {
            console.log(`📄 Found documentId via recursive search (document_id) at ${currentPath}:`, value)
            return value
          }
        }

        // Third pass: recursive search in nested objects
        for (let i = 0; i < entries.length; i += 1) {
          const [key, value] = entries[i]
          const currentPath = path ? `${path}.${key}` : key

          if (typeof value === 'object' && value !== null) {
            const found = findDocumentId(value, currentPath)
            if (found) return found
          }
        }
        return null
      }

      documentId = findDocumentId(zohoData)
    }

    if (!documentId) {
      console.warn('❌ extractDocumentId: Could not find document_id in response structure')
      console.log('🔍 Available keys in zohoData:', Object.keys(zohoData))

      // Log all nested keys for debugging
      const getAllKeys = (obj, prefix = '') => {
        let keys = []
        const entries = Object.entries(obj)
        for (let i = 0; i < entries.length; i += 1) {
          const [key, value] = entries[i]
          const fullKey = prefix ? `${prefix}.${key}` : key
          keys.push(fullKey)
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            keys = keys.concat(getAllKeys(value, fullKey))
          }
        }
        return keys
      }

      console.log('🔍 All nested keys in zohoData:', getAllKeys(zohoData))
    }

    return documentId
  }

  // Extract Zoho request data for contract creation
  const extractZohoContractData = (zohoResponse, offerId, storageId, customerId, offerData) => {
    console.log('🔍 extractZohoContractData called with:', {
      offerId,
      storageId,
      customerId,
      offerData: offerData ? 'provided' : 'missing',
      zohoResponse: zohoResponse ? 'provided' : 'missing'
    })

    const documentId = extractDocumentId(zohoResponse)
    if (!documentId) {
      console.warn('❌ extractZohoContractData: No document ID found, cannot create contract')
      return null
    }

    const zohoData = zohoResponse.data
    let requestData = {}

    // Extract request data from various possible structures
    // Based on logs, the structure is: zohoData.data.requests (object, not array)
    if (zohoData.data && zohoData.data.requests && typeof zohoData.data.requests === 'object') {
      requestData = zohoData.data.requests
      console.log('📋 Using data.requests object:', requestData)
    } else if (zohoData.requests && typeof zohoData.requests === 'object' && !Array.isArray(zohoData.requests)) {
      requestData = zohoData.requests
      console.log('📋 Using requests object:', requestData)
    } else if (Array.isArray(zohoData.requests) && zohoData.requests[0]) {
      [requestData] = zohoData.requests
      console.log('📋 Using requests[0]:', requestData)
    } else {
      requestData = zohoData
      console.log('📋 Using zohoData directly:', requestData)
    }

    // Extract fields with multiple fallback paths
    const requestId = requestData.request_id || documentId
    const actionId = requestData.action_id
                    || (zohoData.data?.requests?.action_id)
                    || (zohoData.requests?.action_id)
                    || null

    // For sign_url, check multiple possible locations
    const signUrl = requestData.sign_url
                   || requestData.signing_url
                   || (zohoData.data?.requests?.sign_url)
                   || (zohoData.data?.requests?.signing_url)
                   || null

    console.log('🔍 Extracted field values:', {
      documentId,
      requestId,
      actionId,
      signUrl,
      requestDataKeys: Object.keys(requestData)
    })

    const contractData = {
      offer: offerId,
      storage: storageId,
      customer: customerId,
      zohoDocumentId: documentId,
      zohoRequestId: requestId,
      zohoSignStatus: 'sent',
      zohoSignUrl: signUrl,
      zohoSentDate: new Date().toISOString(),
      zohoSignerEmail: offerData.email,
      zohoSignerName: `${offerData.firstName || ''} ${offerData.lastName || ''}`.trim(),
      zohoActionId: actionId
    }

    console.log('✅ extractZohoContractData created contract data:', contractData)
    return contractData
  }

  return {
    getZohoPortalUrl,
    getOfferContracts,
    getStorageContractHistory,
    getCustomerContracts,
    getActiveContracts,
    getOfferContractStats,
    hasContracts,
    getStatusVariant,
    getStatusLabel,
    extractDocumentId,
    extractZohoContractData
  }
}
